import React, { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Text, HStack, useToast, Collapse } from '@televet/kibble-ui';
import partial from 'lodash-es/partial';
import { useSaveAutomationDraft } from 'pages/Automations/hooks';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  updateAutomationDraft,
  updateAutomationErrors,
  updateValidationActionWord,
} from 'pages/Automations/state/automationsSlice';
import { useHistory } from 'react-router-dom';
import { useValidateAutomationDraft } from 'pages/Automations/hooks/useValidateAutomationDraft';
import { initialErrorsState, ValidationActionWord } from 'pages/Automations/types/AutomationValidation';
import { useCurrentAutomationId } from 'pages/Automations/hooks/useCurrentAutomationId';
import { useGetAutomationsBasePath } from 'pages/Automations/hooks/useGetAutomationsBasePath';
import { ActionEffectType } from '@televet/shared-types';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

export const AutomationsFooter = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentAutomationId = useCurrentAutomationId();
  const automationPrompts = useAppSelector((state) => state.automations.automationDraft.prompts);
  const isActive = useAppSelector((state) => state.automations.automationDraft.isPublished);
  const { saveAutomationDraft, hasUnsavedChanges, isNewUneditedAutomation, isSaving, originalAutomation } =
    useSaveAutomationDraft();
  const toast = useToast();
  const history = useHistory();
  const { validateAutomationDraft } = useValidateAutomationDraft();
  const { automationsBasePath } = useGetAutomationsBasePath();
  const { addFeatureFlag } = useFeatureFlag();
  const { isWritebackApptNotesSupported } = useIntegrationsProvider();

  const handleCancel = useCallback(() => {
    if (!currentAutomationId || currentAutomationId.startsWith('draft-')) {
      history.push(automationsBasePath);
    } else {
      dispatch(updateAutomationDraft(originalAutomation));
      dispatch(updateAutomationErrors(initialErrorsState));
      toast({
        title: 'Changes Cancelled',
        description: 'Your changes were successfully reverted.',
        status: 'info',
      });
    }
  }, [currentAutomationId, history, automationsBasePath, dispatch, originalAutomation, toast]);

  const handleSave = useCallback(async () => {
    // Only validate on save if the automation is active
    if (isActive) {
      dispatch(updateValidationActionWord(ValidationActionWord.Saving));
      const automationHasErrors = validateAutomationDraft();

      if (automationHasErrors) {
        return;
      }

      // Set EtlConfirmAppointmentWriteback FF required for appt note writebacks
      const hasApptNoteWriteback = !!automationPrompts.some((prompt) =>
        prompt.config.buttons?.some((button) =>
          button.actionEffects?.some((effect) => effect.type === ActionEffectType.AddAppointmentNote),
        ),
      );

      if (hasApptNoteWriteback && isWritebackApptNotesSupported) {
        try {
          await addFeatureFlag(FeatureFlagName.EtlConfirmAppointmentWriteback);
        } catch (e) {
          console.error(`Error adding EtlConfirmAppointmentWriteback FF: ${e}`);
          Sentry.captureException(`Error adding EtlConfirmAppointmentWriteback FF: ${e}`);
        }
      }
    }

    saveAutomationDraft();
  }, [
    isActive,
    saveAutomationDraft,
    dispatch,
    validateAutomationDraft,
    automationPrompts,
    addFeatureFlag,
    isWritebackApptNotesSupported,
  ]);

  return (
    <Collapse in={hasUnsavedChanges || isNewUneditedAutomation} animateOpacity>
      <HStack
        bg="background.default"
        bottom={0}
        boxShadow="mdR"
        className="Automations__Footer"
        justifyContent="flex-end"
        py={3}
        px={5}
        width="100%"
        zIndex="2"
        position="absolute"
      >
        <Text>Unsaved Changes</Text>
        <Button variant="tertiary" onClick={partial(handleCancel)} className="Automations__FieldLeft-CancelButton">
          Cancel
        </Button>
        <Button
          className="Automations__FieldRight-SaveButton"
          mr={2}
          isLoading={isSaving}
          onClick={partial(handleSave)}
        >
          Save
        </Button>
      </HStack>
    </Collapse>
  );
};
